import { useCallback, useContext, useMemo, useState } from "react";
import { Row, Col, Form, Divider } from "antd";
import { useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";

import Hide from "./hide";
import Submit from "./submit";
import Summary from "./summary";
import Contacts from "./contacts";
import Principal from "./principal";
import {
  parseRegistrationForm,
  parseInitialFormValues,
} from "../../../modules/dataFunctions";
import CustomerAlert from "./customerAlert";
import Identificator from "./identificator";
import AddressOrSame from "./addressOrSame";
import Address, { nameField } from "./address";
import Type, { legalEntityName } from "./type";
import { warn } from "../../../modules/messages";
import NationalAccount from "./bank/nationalAccount";
import TermsAndConditions from "./termsAndConditions";
import FurtherInformation from "./furtherInformation";
import CustomTitle from "../../app/generic/customTitle";
import Products, { validateProducts } from "./products";
import InputItem from "../../app/generic/form/inputItem";
import FormSteps, { MIN, MAX, MIDDLE } from "./formSteps";
import { FingerprintContext } from "../../fingerprintProvider";
import { postEntity, putEntity } from "../../../modules/crudOperations";

const Detail = ({ data, oldPassword }) => {
  const intl = useIntl();
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const formItemLayout = useMemo(
    () => ({
      colon: false,
    }),
    []
  );

  const initialContract = useMemo(
    () => parseInitialFormValues(data, oldPassword),
    [data, oldPassword]
  );

  const fingerprint = useContext(FingerprintContext);

  const [formHasError, setFormHasError] = useState();
  const [termsHasError, setTermsHasError] = useState();
  const [productsHasError, setProductsHasError] = useState();
  //const [personalDataHasError, setPersonalDataHasError] = useState();

  const [current, setCurrent] = useState(MIN);

  const [legalEntity, setLegalEntity] = useState(
    (data && data.entity && data.entity.type) || true
  );
  const handleTypeChange = useCallback(
    (e) => {
      setLegalEntity(e.target.value === legalEntityName);
      form.validateFields();
    },
    [form]
  );

  const handleContactPersonChange = useCallback(
    (value) => {
      const current = form.getFieldValue(nameField);
      value &&
        (!current || value.includes(current)) &&
        form.setFieldValue(nameField, value);
    },
    [form]
  );

  const [id] = useState(data && data.id);
  const [insolvency, setInsolvecy] = useState();
  const [successSend, setSuccessSend] = useState();
  const [termsAgreeDateTime, setTermsAgreeDateTime] = useState(
    data && data.agreeWithTerms
  );
  /*const [personalDataAgreeDateTime, setPersonalDataAgreeDateTime] = useState(
    data && data.agreeWithPersonalData
  );*/
  const handleDataSend = useCallback(
    (values, onSetLoading, sent) => {
      const data = parseRegistrationForm(
        id,
        values,
        fingerprint,
        termsAgreeDateTime,
        //personalDataAgreeDateTime,
        sent
      );
      const message = sent
          ? intl.formatMessage({
              id: "registration.submit.result",
              defaultMessage: "Registrace byla úspěšné zaevidována v DHL.",
            })
          : intl.formatMessage({
              id: "registration.save.result",
              defaultMessage: "Registrace byla úspěšné uložena.",
            }),
        errorMessage = sent
          ? intl.formatMessage({
              id: "registration.submit.result.error",
              defaultMessage: "Při odesílání registrace nastala chyba.",
            })
          : intl.formatMessage({
              id: "registration.save.result.error",
              defaultMessage: "Při ukládání registrace nastala chyba.",
            });
      const processStatus = (res, status) => {
        setSuccessSend(sent && res && res !== "e");
        setInsolvecy(res && res.insolvencyRegister);
        status === 409 &&
          warn(
            intl.formatMessage({
              id: "registration.save.result.sms",
              defaultMessage: "Nesprávný potvrzovací SMS kód.",
            })
          );
      };
      id
        ? putEntity(
            "contract",
            id,
            onSetLoading,
            data,
            {},
            "",
            "",
            true,
            processStatus,
            message,
            errorMessage
          )
        : postEntity(
            "contract",
            onSetLoading,
            data,
            "",
            "",
            true,
            (res, status) => {
              res &&
                navigate(`/form/${res.id}`, {
                  state: { smsCode: data.smsCode },
                });
              processStatus(res, status);
            },
            message,
            errorMessage
          );
    },
    [id, intl, navigate, fingerprint, termsAgreeDateTime]
  );

  const [saveLoading, setSaveLoading] = useState();
  const handleSaveData = useCallback(
    (values) => {
      handleDataSend(values, setSaveLoading);
    },
    [handleDataSend]
  );

  const [loading, setLoading] = useState();
  const handleSubmit = useCallback(
    (formData) => {
      setLoading(true);
      const values = formData.values ? formData.values : formData;
      //setPersonalDataHasError(!values.personalData);
      setTermsHasError(!values.termsAndConditions);
      if (!formData.errorFields) {
        setFormHasError();
        if (validateProducts(values)) {
          setProductsHasError();
          if (values.termsAndConditions) {
            handleDataSend(values, setLoading, true);
          } else {
            setLoading();
          }
        } else {
          setProductsHasError(true);
          setLoading();
        }
      } else {
        setFormHasError(true);
        setLoading();
      }
    },
    [handleDataSend]
  );

  return (
    <Row>
      <Col offset={2} span={20}>
        <Form
          form={form}
          layout="vertical"
          {...formItemLayout}
          onFinish={handleSubmit}
          onFinishFailed={handleSubmit}
          initialValues={initialContract}
          disabled={successSend || (data && data.sent)}
        >
          <InputItem hidden fieldName="password" />
          <CustomerAlert
            show={data && data.sent}
            textId="registration.sent.info"
            defautTextMessage="Registrace byla již odeslána do DHL, tudíž ji není možné editovat."
          />
          <FormSteps
            current={current}
            onSetCurrent={setCurrent}
            formHasError={formHasError}
            productsHasError={productsHasError}
            consentsHasError={termsHasError /*|| personalDataHasError*/}
          />
          <Hide show={current === MIN}>
            <Row>
              <Col xs={24} lg={16} xl={14} xxl={9}>
                <Type
                  onTypeChange={handleTypeChange}
                  formItemLayout={formItemLayout}
                />
                <Identificator
                  form={form}
                  legalEntity={legalEntity}
                  entity={data && data.entity}
                  formItemLayout={formItemLayout}
                />
                <Divider />
                <CustomTitle
                  id="registration.contacts.principal"
                  defaultMessage="Příkazce"
                />
                <Principal
                  form={form}
                  formItemLayout={formItemLayout}
                  initialPrincipal={data && data.principal}
                  onContactPersonChange={handleContactPersonChange}
                />
                <Divider />
                {legalEntity ? (
                  <CustomTitle
                    id="registration.address"
                    defaultMessage="Sídlo příkazce"
                  />
                ) : (
                  <CustomTitle
                    id="registration.address.home"
                    defaultMessage="Trvalé bydliště"
                  />
                )}
                <Address
                  disabled
                  form={form}
                  formItemLayout={formItemLayout}
                  initialAddress={
                    data && data.principal && data.principal.address
                  }
                />
                <Divider />
                <CustomTitle
                  id="registration.bank"
                  defaultMessage="Bankovní spojení"
                />
                {/* <Bank form={form} formItemLayout={formItemLayout} /> */}
                <NationalAccount formItemLayout={formItemLayout} />
                <Divider />
                <AddressOrSame
                  form={form}
                  formItemLayout={formItemLayout}
                  initialAddress={data && data.invoiceAddress}
                  initialSameAddress={
                    data && data.invoiceAddressSameAsPrincipal
                  }
                />
                <Divider />
                <AddressOrSame
                  pickup
                  form={form}
                  formItemLayout={formItemLayout}
                  initialAddress={data && data.pickupAddress}
                  initialSameAddress={data && data.pickupAddressSameAsPrincipal}
                />
                <Divider />
                <CustomTitle
                  id="registration.contacts"
                  defaultMessage="Kontakty"
                />
                <Contacts formItemLayout={formItemLayout} />
                <Divider />
                <CustomTitle
                  id="registration.products"
                  defaultMessage="Produkty"
                />
                <Products
                  hasError={productsHasError}
                  formItemLayout={formItemLayout}
                />
                <Divider />
                <CustomTitle
                  id="registration.furtherInformation"
                  defaultMessage="Další informace"
                />
                <FurtherInformation
                  legalEntity={legalEntity}
                  formItemLayout={formItemLayout}
                />
              </Col>
            </Row>
          </Hide>
          <Hide show={current === MIDDLE}>
            <>
              <TermsAndConditions
                termsHasError={termsHasError}
                formItemLayout={formItemLayout}
                onSetTermsHasError={setTermsHasError}
                onTermsAgreeChange={setTermsAgreeDateTime}
              />
              {/* <Divider />
               <PersonalData
                formItemLayout={formItemLayout}
                personalDataHasError={personalDataHasError}
                onSetPersonalDataHasError={setPersonalDataHasError}
                onPersonalDataAgreeChange={setPersonalDataAgreeDateTime}
              /> */}
            </>
          </Hide>
          <Hide show={current === MAX}>
            <Summary
              insolvency={insolvency}
              sent={successSend || (data && data.sent)}
            />
          </Hide>
          <Divider />
          <Submit
            id={id}
            form={form}
            loading={loading}
            current={current}
            oldPassword={oldPassword}
            successSend={successSend}
            onSetCurrent={setCurrent}
            saveLoading={saveLoading}
            lastPage={current === MAX}
            onDataSend={handleSaveData}
            onFormHasError={setFormHasError}
            initialContract={initialContract}
            onTermsHasError={setTermsHasError}
            onProductsHasError={setProductsHasError}
          />
        </Form>
      </Col>
    </Row>
  );
};

export default Detail;
